class Events {
    constructor() {

    }

    init(helpers) {
        const self = this;

        self.helpers = helpers;

        self.addDimensionsToSvgs();
        self.ariaHideSvgs();
        self.fixTopBarAccessibilityIssues(self);
        self.removeUnwantedElements();
        self.fixFilterBarAccessibilityIssues(self);
        self.removeColonsFromMetaLabels();
        self.preventEcpFilterBarSubmission();

        let observer,
            ajaxContainerParent = document.querySelector('main > .content');

        if (!ajaxContainerParent) return;

        /* The Events Calendar uses some kind of black box AJAX handler to update the view (between "List", "Month, "Day" - and when applying filters).

        When testing, I noticed that my accessibility fixes were not persisting when this AJAX updating occured. I determined/deduced that this is because their AJAX deletes (and reinjects) a `<div>` every time the view is changed or a filter is applied.

        This MutationObserver listens for that deletion/reinjection and reruns the accessibility fixes (so that they persist across all AJAX updates) */
        observer = new MutationObserver(mutations => {

            // Listen for the `.tribe-events-view` container inside this to be replaced (via AJAX)
            if (mutations[0].type !== 'childList') return;

            // Rerun these functions every time `.tribe-events-view` is replaced via AJAX (when user changes view, etc.)
            self.addDimensionsToSvgs();
            self.ariaHideSvgs();
            self.fixTopBarAccessibilityIssues(self);
            self.removeUnwantedElements();
            self.fixFilterBarAccessibilityIssues(self);
        });

        observer.observe(ajaxContainerParent, { childList: true, subtree: false });
    }

    addDimensionsToSvgs() {
        let dimensionlessSvgs = document.querySelectorAll('.tribe-common-c-svgicon[viewBox]:not([width]:not([height])');

        /* The Events Calendar's SVGs are massive when viewing the page with alternate and/or _no_ CSS. (note: some users with specific visual disabilities apply custom CSS in their browsers to make pages more readable). This is a quick, "freebie" patch to fix that issue */
        dimensionlessSvgs.forEach(svg => {
            let viewBox = svg.getAttribute('viewBox'),
                viewBoxValues = viewBox.split(' '),
                width = viewBoxValues[2],
                height = viewBoxValues[3];

            if (!width || !height) return;

            svg.setAttribute('width', width);
            svg.setAttribute('height', height);
        });
    }

    ariaHideSvgs() {
        let svgs = document.querySelectorAll('.tribe-common-c-svgicon');

        // The Events Calendar does not properly hide decorative `<svg>`s from assistive technology users. This function ensures that these are correctly hidden.
        svgs.forEach(svg => {
            if (svg.querySelector('title')) return;
            if (svg.hasAttribute('aria-label')) return;
            if (svg.hasAttribute('aria-labelledby')) return;

            svg.setAttribute('aria-hidden', 'true');
            svg.setAttribute('focusable', 'false');
        });
    }

    fixFilterBarAccessibilityIssues(self) {
        let unwantedElements = document.querySelectorAll('.tribe-filter-bar-c-filter__toggle-label-colon, .tribe-filter-bar-c-filter__toggle-selections, .tribe-filter-bar-c-pill__pill-label-colon, .tribe-filter-bar-c-pill__pill-selections');

        // These child elements were causing accessibility issues such as: making their parent element's accessible name nonsensical and/or confusing to screen reader users. Removing them fixes those issues.
        unwantedElements.forEach(element => {
            element.remove();
        });

        let filterToggleWrappers = document.querySelectorAll('.tribe-filter-bar-c-filter__toggle-wrapper');

        /* These elements had no `role`. They should be `<h*>` elements so that they have a `role` and are also included in the document outline (and reachable by assistive technology users via "Heading" navigation - like the headings on our other Archive pages).

        Also: they _look_ like headings out-of-the-box (so they should just _be_ headings, so that there's correspondance between the visual presentation and the HTML semantics. See WCAG's "Understandable" criteria) */
        filterToggleWrappers.forEach(wrapper => {
            self.helpers.changeHTMLTag(wrapper, 'H3');
        });

        let checkedCheckboxes = document.querySelectorAll('.tribe-filter-bar-c-checkbox > input[type="checkbox"].tribe-common-form-control-checkbox__input[checked]:not([aria-disabled="true"]')

        if (!checkedCheckboxes) return;

        /* The Events Calendar hijacks these `<input type="checkbox">` elements' default behaviour (making it impossible to remove the `checked` property via `click`).

        This code adds `aria-disabled="true"` to these inputs to let screen reader users know they are now "disabled". It also adds helper text to notify the user of the alternative path they'll need to take (through the page) to "uncheck" these checkboxes.

        Additionally: this helper text is explicitly linked to the `<input>` via `aria-describedby` (and a unique `id` on the helper text) to ensure that the helper text is announced when the input is focused.

        Finally: we make the helper text element "invisible" to screen reader users (via `aria-hidden`) and visual users (via `class="visually-hidden"`) so that it's not read twice to screen reader users and is not shown to visual-only users. */
        checkedCheckboxes.forEach(checkbox => {
            let helperTextId = `${checkbox.id}__helper-text`,
                helperText = self.helpers.createElementFromString(`<span id="${helperTextId}" class="visually-hidden" aria-hidden="true"> ${i18n['events']['filter_bar']['disabled_checkbox_helper_text']}</span>`);

            checkbox.setAttribute('aria-disabled', 'true');
            checkbox.setAttribute('aria-describedby', helperTextId);
            checkbox.addEventListener('click', e => {
                e.preventDefault();
            });

            checkbox.parentElement.appendChild(helperText);
        });
    }

    fixTopBarAccessibilityIssues(self) {
        // Exit early if the current page is not an Events archive page
        if (!document.body.classList.contains('post-type-archive-tribe_events')) return;

        let helperText = document.querySelectorAll('.tribe-events-c-view-selector__list .helper-text') ?? [];

        // When the view is updated via The Events Calendar's AJAX handling, remove our existing "helper text" `<span>` (we'll add it back in - in the right place - once again [further down within this function])
        helperText.forEach(element => {
            element.remove();
        });

        let todayLink = document.querySelector('a.tribe-events-c-top-bar__today-button');

        /* `title` attribute is not necessary here. Like `aria-label`, it overrides the element's built-in accessible name (the `textContent` of the element).

        Including this attribute (with its default The Events Calendar -generated content) causes accessibility issues for users (visual content not matching screen reader -read content when the element is focused) */
        todayLink.removeAttribute('title');

        if (todayLink.textContent !== '') {
            // If the element has text content inside it, just use that as its accessible name
            todayLink.removeAttribute('aria-label');
        }

        let datePickerButton = document.querySelector('button.tribe-events-c-top-bar__datepicker-button');

        /* `title` attribute is not necessary here. Like `aria-label`, it overrides the element's built-in accessible name (the `textContent` of the element).

        Including this attribute (with its default The Events Calendar -generated content) causes accessibility issues for users (visual content not matching screen reader -read content when the element is focused) */
        datePickerButton.removeAttribute('title');

        if (datePickerButton.textContent !== '') {
            // If the element has text content inside it, just use that as its accessible name
            datePickerButton.removeAttribute('aria-label');
        }

        let currentViewLink = document.querySelector('.tribe-events-c-view-selector__list-item--active > .tribe-events-c-view-selector__list-item-link'),
            helperSpan = self.helpers.createElementFromString(`<span class="helper-text visually-hidden">${i18n['events']['current_view']}</span>`);

        // Add a `.visually-hidden` `<span>` inside the current view's link so that the user is notified of which of the view is currently active on screen
        currentViewLink.insertBefore(helperSpan, currentViewLink.children[1]);

        let filterToggleLabels = document.querySelectorAll('.tribe-filter-bar-c-filter__toggle-icon-text');

        /* The Events Calendar added in some additional "state" labels here. These are unnecessary; that state is communicated (robustly, and correctly) already via the `aria-expanded` attribute.

        In addition to being unnecessary, these labels were also _both_ being announced regardless of the `aria-expanded` state of the `<button>` they're inside of, making the `<button>`'s accessible name super confusing to screen reader users (something like "Topic - button collapsed. Closed. Opened."), which is - in itself - another accessibility issue.

        This code fixes those issues. */
        filterToggleLabels.forEach(label => {
            label.remove();
        });
    }

    removeUnwantedElements() {
        let unwantedElements = document.querySelectorAll('.tribe-events-single-event-title, .tribe-events-schedule, #tribe-events-header');

        // These elements were either 1) empty (and causing layout issues), or 2) were headings of the wrong heading level (thereby causing accessibility issues)
        unwantedElements.forEach(element => {
            element.remove();
        });

        let listContainer = document.querySelector('.tribe-events-calendar-list');
        if (!listContainer) return;
        /* This element was being added to the page empty in some contexts (when switching views), and it was breaking the layout.

        This code checks to see if the container is empty, and - if it is - it removes it (I tried just using `:empty` in the stylesheet, but it didn't detect this difference correctly) */
        if (!listContainer.children[0]) {
            listContainer.hidden = true;
        } else {
            listContainer.hidden = false;
        }
    }

    removeColonsFromMetaLabels() {
        let labels = document.querySelectorAll('.tribe-events-meta-group dt');

        labels.forEach(label => {
            label.textContent = label.textContent.replace(':', '');
        });

    }

    /**
    * Prevents the filter bar from submitting when the user
    * presses the "Enter" key on the global search form
    */
    preventEcpFilterBarSubmission() {
        jQuery('.search-form .control').keydown(function (event) {
            if (event.keyCode === 13) {
                jQuery('.search-form').submit()
            }
        });
    }
}

export default Events;
